<template>
  <div class="item">
    <PageHeading
      :title="item_name"
      :subheading="cat_name"
    ></PageHeading>
    <h3 class="label">Sua avaliação:</h3>
    <ProgressBar
      class="progress-bar"
      :grade="grade"
      :goal="goal"
      :grade_color="grade_color"
      :goal_color="goal_color"
    />
    <div class="dates-container">
      <p><span :style="{ 'color': grade_color }">Avaliação:</span> {{ eval_date }}</p>
      <p><span :style="{ 'color': grade_color }">Meta:</span> {{ goal_date }}</p>
    </div>
    <v-divider></v-divider>
    <v-row no-gutters>
      <v-col cols="10">
        <div class="text-h5 text-left mt-2">Ações:</div>
      </v-col>
      <v-spacer/>
      <v-col cols="2" align-self="end">
        <!-- Help dialog -->
        <v-dialog
          fullscreen
          hide-overlay
          v-model="help_dialog"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-on="on"
              v-bind="attrs"
              icon
              small
            >
              <v-icon>mdi-help-circle-outline</v-icon>
            </v-btn>
          </template>

          <!-- Help card with sugestions -->
          <v-card>
            <v-toolbar
              dark
              tile
              color="primary"
            >
              <v-btn icon dark @click="help_dialog = false">
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-toolbar-title>Sugestões</v-toolbar-title>
            </v-toolbar>

            <v-container>
              <v-progress-circular
                v-if="suggestions_loading"
                indeterminate
                size="50"
              ></v-progress-circular>
              <v-row dense v-else>
                <v-col
                  v-for="(suggestion, i) in suggestions"
                  :key="suggestion.id"
                  cols="12"
                >
                  <suggestion-preview
                    :title="suggestion.title"
                    :description="suggestion.description"
                    @use="use_suggestion(i)"
                  ></suggestion-preview>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>

    <!-- Task list -->
    <v-container>
      <v-row v-if="all_tasks_loading" dense>
        <v-col v-for="n in 2" :key="n" cols="12">
          <v-skeleton-loader elevation="2" type="article"></v-skeleton-loader>
        </v-col>
      </v-row>
      <v-row v-else dense class="mb-10">
        <v-col cols="12" v-for="(task, index) in tasks" :key="index">
          <ContentPreview
            :title="task.title"
            :categoryName="task_get_category_name(task)"
            :itemName="task_get_item_name(task)"
            :description="task.description"
            :time="task.time"
            :date="task.due_date"
            :color="grade_color"
            :loading="task_loading.has(task.id)"
            :week-days="task_get_week_days(task)"
            @delete="task_delete(task)"
          ></ContentPreview>
        </v-col>
      </v-row>
    </v-container>

    <!-- Add task FAB -->
    <v-btn
      class="mx-2"
      fab
      fixed
      bottom
      right
      dark
      color="#802C12"
      @click="task_add(path)"
    >
      <v-icon dark>mdi-plus</v-icon>
    </v-btn>

    <!-- Notes FAB -->
    <v-btn
      class="mx-2"
      fab
      fixed
      bottom
      left
      dark
      color="#802C12"
      @click="notes()"
    >
      <v-icon dark>mdi-card-text</v-icon>
    </v-btn>
  </div>
</template>
<script>
import Vue from "vue";
import { mapGetters } from "vuex";

import PageHeading from "@/components/PageHeading.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import ContentPreview from "@/components/ContentPreview.vue";
import SuggestionPreview from "@/components/SuggestionPreview.vue";

import TasksMixin from "@/mixins/tasks.mixin";
import { firestore } from "@/firebase";
import * as db from "@/db/constants";

export default Vue.extend({
  name: "SingleItem",
  components: {
    ProgressBar,
    PageHeading,
    ContentPreview,
    "suggestion-preview": SuggestionPreview,
  },
  mixins: [TasksMixin],
  data() {
    return {
      cat_name: "",
      item_name: "",
      grade: 0,
      goal: 0,
      eval_date: "14/05/2020",
      goal_date: "01/07/2020",
      grade_color: "",
      goal_color: "",
      all_tasks_loading: false,
      help_dialog: false,
      suggestions_loading: true,
      suggestions: [],
    }
  },
  mounted() {
    if (!this.loggedIn)
      console.errro("Tryied to load SingleItem.vue witout a user logged in.");

    this.setup();
  },
  watch: {
    help_dialog(newVal) {
      if (newVal) {
        this.suggestions_loading = true;
        this.load_suggestions()
          .then(() => this.suggestions_loading = false);
      }
    }
  },
  computed: {
    tasks() {
      return Object.values(this.tasks_all)
        .filter(task => 
          task.associations.some(assoc => assoc.path === this.path)
        )
        .sort((a, b) => {
          const a_next = this.task_get_next_date(a);
          const b_next = this.task_get_next_date(b);
          return a_next < b_next ? -1 : (a_next > b_next ? 1 : 0);
        });
    },
    path() {
      return `/${this.context_id}/${this.cat_id}/${this.item_id}`;
    },
    group_id() { return this.$route.params.group_id; },
    context_id() { return this.$route.params.context_id; },
    cat_id() { return this.$route.params.cat_id; },
    item_id() { return this.$route.params.item_id; },
    ...mapGetters([
      "loggedIn"
    ]),
    context() { return this.$store.getters[`user/${this.group_id}/${this.context_id}/cached`]; },
  },
  methods: {
    async setup() {
      this.all_tasks_loading = true;
      this.task_cache_all().then(() => this.all_tasks_loading = false);

      if (this.loggedIn) {
        const [grade_color, goal_color, cat_name, item_name] = await Promise.all([
          this.$store.dispatch(`templates/get`, {
            path: `${this.context_id}/categories/${this.cat_id}/grade_color`
          }),
          this.$store.dispatch(`templates/get`, {
            path: `${this.context_id}/categories/${this.cat_id}/goal_color`
          }),
          this.$store.dispatch(`templates/get`, {
            path: `${this.context_id}/categories/${this.cat_id}/name`
          }),
          this.$store.dispatch(`templates/get`, {
            path: `${this.context_id}/categories/${this.cat_id}/items/${this.item_id}/name`
          }),
        ]);

        const cat = this.context.categories.find(cat => cat.id === this.cat_id);
        const item = cat.items.find(item => item.id === this.item_id);
        this.grade = item.grade;
        this.goal = item.goal;
        this.grade_color = grade_color;
        this.goal_color = goal_color;
        this.cat_name = cat_name;
        this.item_name = item_name;

        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        console.warn("SingleCategory.vue was loaded with no user logged in.");
      }
    },
    async load_suggestions() {
      const suggestions = await firestore
        .collection(db.TEMPLATES_COLLECTION)
        .doc(db.TEMPLATE_DEFAULT)
        .collection(db.SUGGESTIONS_COLLECTION)
        .where("path", "==", this.path)
        .get();
      
      const all_suggestions = [];
      suggestions.forEach(doc => {
        all_suggestions.push({
          id: doc.id,
          ...doc.data(),
        });
      });
      this.suggestions = all_suggestions;
    },
    notes() {
      this.$router.push({
        name: "Notes",
        params: {
          group_id: this.group_id,
          context_id: this.context_id
        },
        query: {
          path: this.path,
        }
      });
    },
    use_suggestion(idx) {
      this.task_add(this.path, this.suggestions[idx].title);
    },
  }
});
</script>
